import React, { useRef, useEffect } from 'react';
import { EditorView } from '@codemirror/view';
import { EditorState, Compartment } from '@codemirror/state';
import { createEditorExtensions } from './editorSetup';

// Per-section state cache: sectionKey → { state, doc }
// Preserves undo/redo history when switching between sections
const sectionStateCache = new Map();

// Compartment for dynamic callback extensions (reconfigurable)
const callbackCompartment = new Compartment();

function makeCallbackExtensions(onChangeRef, onExitRef) {
  return [
    EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        onChangeRef.current?.(update.state.doc.toString());
      }
    }),
    EditorView.domEventHandlers({
      keydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          onExitRef.current?.();
          return true;
        }
      },
    }),
  ];
}

/**
 * Inline CodeMirror editor for a single markdown section.
 * Mounts on click, auto-focuses, Escape to exit.
 * Preserves undo/redo history when switching between sections via sectionKey.
 */
const SectionEditor = ({ content, onChange, onSave, onExit, onViewReady, sectionKey }) => {
  const editorRef = useRef(null);
  const viewRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onSaveRef = useRef(onSave);
  const onExitRef = useRef(onExit);
  const sectionKeyRef = useRef(sectionKey);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onSaveRef.current = onSave; }, [onSave]);
  useEffect(() => { onExitRef.current = onExit; }, [onExit]);

  useEffect(() => {
    if (!editorRef.current) return;

    const baseExtensions = createEditorExtensions(
      () => onSaveRef.current?.(),
      () => {}
    );

    const cbExtensions = makeCallbackExtensions(onChangeRef, onExitRef);

    let state;
    const cached = sectionKey ? sectionStateCache.get(sectionKey) : null;

    if (cached && cached.doc === (content || '')) {
      // Content matches cached → restore state with full undo history,
      // then reconfigure callbacks to use current refs
      state = cached.state;
    } else {
      // Fresh state
      state = EditorState.create({
        doc: content || '',
        extensions: [
          ...baseExtensions,
          callbackCompartment.of(cbExtensions),
        ],
      });
    }

    const view = new EditorView({
      state,
      parent: editorRef.current,
    });

    // If restored from cache, reconfigure callback compartment to use fresh refs
    if (cached && cached.doc === (content || '')) {
      view.dispatch({
        effects: callbackCompartment.reconfigure(cbExtensions),
      });
    }

    viewRef.current = view;
    if (onViewReady) onViewReady(view);

    // Auto-focus
    requestAnimationFrame(() => {
      view.focus();
    });

    return () => {
      // Cache state before destroying (preserves undo history for next mount)
      if (sectionKeyRef.current && viewRef.current) {
        const currentDoc = viewRef.current.state.doc.toString();
        sectionStateCache.set(sectionKeyRef.current, {
          state: viewRef.current.state,
          doc: currentDoc,
        });
      }
      view.destroy();
      viewRef.current = null;
      if (onViewReady) onViewReady(null);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={editorRef}
      className="section-cm-container"
      onClick={(e) => e.stopPropagation()}
    />
  );
};

// Clear cached state for a document (call when switching documents)
export function clearSectionStateCache() {
  sectionStateCache.clear();
}

export default SectionEditor;
